import { useState, type FormEvent, type JSX } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { SPECIES_META, type DuckColor, type DuckSpecies } from '@duck/contracts';
import { api, type ApiError } from '../../lib/api';
import { usePageMeta } from '../../lib/page-meta';
import { useAuthStore } from '../../state/auth.store';
import { AuthShell, ErrorText, Field, SubmitButton } from '../../components/ui';
import { DuckPicker } from '../onboarding/DuckPicker';
import { SpeciesPicker, CritterPreview } from '../onboarding/CharacterPicker';
import { ROOM_TYPES, type RoomLevelId } from '../rooms/room-types';

/**
 * Create an account (ТЗ §2): a USERNAME ("Логин", the private credential) + a separate PUBLIC NICKNAME
 * (what shows in chat / over your duck — keeps case + allows spaces, emoji, special characters) +
 * password + confirm. Minimal by design — no email/confirmation/profile (ТЗ §10). The username is never
 * shown publicly (exposing the credential is unsafe); the confirm field is a CLIENT-SIDE equality check
 * only, never sent. On success the server also creates the account's personal "home" room.
 */
export function RegisterPage(): JSX.Element {
  usePageMeta({ title: 'Create your account — Duck Duck Space', noindex: true });
  const navigate = useNavigate();
  const location = useLocation();
  const setAuth = useAuthStore((s) => s.setAuth);
  const lastIdentity = useAuthStore((s) => s.lastIdentity);
  const [login, setLogin] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [theme, setTheme] = useState<RoomLevelId>('medieval_castle'); // new rooms default to the castle
  // Your critter (ADR-0024) — pre-selected from this device's last pick so returning players keep their
  // look, then persisted on the account at registration and re-editable later from the lobby.
  const [species, setSpecies] = useState<DuckSpecies>(lastIdentity?.species ?? 'duck');
  const [duckColor, setDuckColor] = useState<DuckColor>(lastIdentity?.duckColor ?? 'yellow');
  const [localError, setLocalError] = useState<string | null>(null);

  // Picking an animal snaps the tint to its natural default (still recolourable below). Mirrors JoinPage.
  const onSpeciesChange = (next: DuckSpecies): void => {
    setSpecies(next);
    setDuckColor(SPECIES_META[next].defaultColor);
  };

  // If a specific room link bounced them here to sign up, return THERE after registering; otherwise land
  // in the LOBBY (the hub) — NOT auto-dropped into the brand-new home room, so a fresh account chooses
  // where to go (its home room is waiting under "Your rooms"). `fromState` is threaded through the
  // login↔register links so the room link survives whichever page the visitor lands on first.
  const fromState = (location.state as { from?: { pathname?: string } } | null)?.from ?? null;
  const from = fromState?.pathname ?? null;

  const mutation = useMutation({
    mutationFn: api.register,
    onSuccess: (result) => {
      setAuth(result);
      navigate(from ?? '/lobby', { replace: true });
    },
  });

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);
    const trimmedLogin = login.trim();
    const trimmedNick = displayName.trim();
    // Friendly client-side validation (the server's Zod schema + DB are authoritative — ТЗ §2).
    if (trimmedLogin.length < 3) {
      setLocalError('Username must be at least 3 characters');
      return;
    }
    if (trimmedNick.length < 2) {
      setLocalError('Nickname must be at least 2 characters');
      return;
    }
    if (password.length < 8) {
      setLocalError('Password must be at least 8 characters');
      return;
    }
    if (password !== confirm) {
      setLocalError('Passwords do not match');
      return;
    }
    mutation.mutate({ login: trimmedLogin, displayName: trimmedNick, password, theme, duckColor, species });
  };

  // A failed mutation (e.g. 409 "that login is already taken") wins over a stale local hint.
  const error = mutation.error ? (mutation.error as ApiError).message : localError;

  return (
    <AuthShell title="Create your account" subtitle="A username to log in, and a nickname others will see">
      <form onSubmit={onSubmit}>
        <ErrorText>{error}</ErrorText>
        {/* Your character first (ADR-0024): the live WYSIWYG critter + its pickers, so the very first
            thing you do is a delightful, reversible choice of who you'll be — then room type, then the
            credentials. Persisted on the account; re-editable later from the lobby's "Your character". */}
        <CritterPreview species={species} color={duckColor} />
        <SpeciesPicker value={species} onChange={onSpeciesChange} />
        <DuckPicker value={duckColor} onChange={setDuckColor} />
        <div className="mb-4">
          <span className="mb-1.5 block text-sm font-medium text-white/80">Room type</span>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3" role="radiogroup" aria-label="Room type">
            {ROOM_TYPES.map((rt) => {
              const selected = theme === rt.id;
              return (
                <button
                  key={rt.id}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  onClick={() => setTheme(rt.id)}
                  className={`flex flex-col items-start gap-0.5 rounded-xl border p-3 text-left transition ${
                    selected
                      ? 'border-amber-300 bg-amber-300/10 ring-1 ring-amber-300/40'
                      : 'border-white/10 bg-white/5 hover:border-white/25'
                  }`}
                >
                  <span className="text-xl" aria-hidden>
                    {rt.icon}
                  </span>
                  <span className="text-sm font-semibold text-white">{rt.label}</span>
                  <span className="text-xs text-white/55">{rt.blurb}</span>
                </button>
              );
            })}
          </div>
        </div>
        <Field
          label="Username (private — for logging in)"
          autoComplete="username"
          required
          value={login}
          onChange={(e) => {
            setLogin(e.target.value);
            setLocalError(null);
          }}
        />
        <Field
          label="Nickname (shown in chat)"
          autoComplete="nickname"
          placeholder="e.g. Captain Quack 🦆"
          required
          value={displayName}
          onChange={(e) => {
            setDisplayName(e.target.value);
            setLocalError(null);
          }}
        />
        <Field
          label="Password"
          type="password"
          autoComplete="new-password"
          required
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            setLocalError(null);
          }}
        />
        <Field
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          required
          value={confirm}
          onChange={(e) => {
            setConfirm(e.target.value);
            setLocalError(null);
          }}
        />
        <SubmitButton loading={mutation.isPending}>Create account</SubmitButton>
      </form>
      <p className="mt-5 text-center text-sm text-white/60">
        Already have an account?{' '}
        <Link
          to="/login"
          state={fromState ? { from: fromState } : undefined}
          className="font-medium text-amber-300 hover:text-amber-200"
        >
          Log in
        </Link>
      </p>
    </AuthShell>
  );
}
